import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'wouter';
import { Button } from '@/components/ui/button';
import { getConsent, setConsent } from '@/lib/analytics';
import { langHref } from '@/lib/i18n-routing';

// Sits outside LangContext (it's mounted once at the app root, above the router),
// so it reads the active language straight from i18next rather than useLangHref.
export default function CookieConsent() {
  const { t, i18n } = useTranslation();
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    setVisible(getConsent() === null);
  }, []);

  // The sticky offer bar (StickyOfferBar.tsx) hides itself while this banner is open, via
  // this attribute and a rule in index.css, so the two never stack on top of each other.
  useEffect(() => {
    const root = document.documentElement;
    if (visible) root.setAttribute('data-cookie-banner', 'open');
    else root.removeAttribute('data-cookie-banner');
    return () => root.removeAttribute('data-cookie-banner');
  }, [visible]);

  if (!visible) return null;

  const lang = i18n.language.split('-')[0];
  const privacyHref = langHref(lang, '/privacy-policy');

  const decide = (status: 'accepted' | 'rejected') => {
    setConsent(status);
    setVisible(false);
  };

  return (
    <div
      role="dialog"
      aria-label={t('cookieConsent.title')}
      className="fixed inset-x-0 bottom-0 z-50 border-t border-border bg-background/95 backdrop-blur supports-[backdrop-filter]:bg-background/80 shadow-[0_-4px_24px_rgba(0,0,0,0.25)]"
    >
      <div className="mx-auto flex max-w-6xl flex-col gap-4 px-4 py-4 sm:flex-row sm:items-center sm:justify-between sm:px-6">
        <p className="text-sm text-muted-foreground">
          {t('cookieConsent.message')}{' '}
          <Link
            href={privacyHref}
            className="underline text-foreground hover:text-primary"
            aria-label={t('privacyPolicy.title')}
          >
            {t('privacyPolicy.title')}
          </Link>
        </p>
        <div className="flex flex-shrink-0 gap-3">
          <Button variant="outline" size="sm" onClick={() => decide('rejected')}>
            {t('cookieConsent.reject')}
          </Button>
          <Button size="sm" onClick={() => decide('accepted')}>
            {t('cookieConsent.accept')}
          </Button>
        </div>
      </div>
    </div>
  );
}
